import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import SectionDivider from "../components/SectionDivider";
import { fetchServices, getMediaUrl } from "../api/services";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  accent: "#F4A896",
  textMain: "#2E2E2E",
  textSub: "#4B5563",
  background: "#F7FAF9",
};

const ServiceDescription = ({ description }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      if (textRef.current.scrollHeight > textRef.current.clientHeight) {
        setShowButton(true);
      }
    }
  }, [description]);

  return (
    <div>
      <div
        ref={textRef}
        className="mt-4 text-base leading-relaxed formatted-content"
        style={{ 
          color: COLORS.textSub, 
          display: isExpanded ? 'block' : '-webkit-box', 
          WebkitLineClamp: isExpanded ? 'unset' : 5, 
          WebkitBoxOrient: 'vertical', 
          overflow: 'hidden' 
        }}
        dangerouslySetInnerHTML={{ __html: description }}
      />
      {showButton && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 text-sm font-semibold transition-colors focus:outline-none"
          style={{ color: COLORS.primary }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const BLOB_COLORS = [
  COLORS.accent,
  "#8CCBFF",
  COLORS.primary,
  COLORS.secondary,
  "#A8E6CF",
];

const Services = () => {
  const [servicesList, setServicesList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await fetchServices();
        setServicesList(data);
      } catch (error) {
        console.error("Failed to fetch services:", error);
      } finally {
        setLoading(false);
      }
    };
    loadServices();
  }, []);

  const WHATSAPP_NUMBER = "919098114568"; // replace with your real number
  const WHATSAPP_MESSAGE = "Hello, I want to enquire about";
  return (
    <>
    <section className="pt-10 pb-16 sm:pt-14 sm:pb-20 px-4 sm:px-6" style={{ background: COLORS.background }}>
<div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
    
    {/* Small Pill Badge */}
  {/* Badge */}
<div
  className="inline-flex items-center px-4 py-1.5 sm:px-5 sm:py-2 rounded-full text-[10px] sm:text-xs font-semibold tracking-[0.12em] uppercase"
  style={{
    backgroundColor: `${COLORS.primary}15`,
    color: COLORS.primary,
    border: `1px solid ${COLORS.primary}40`,
  }}
>
  Personalized Healthcare
</div>

{/* Heading */}
<h2
  className="mt-4 sm:mt-6 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight"
  style={{ color: COLORS.textMain }}
>
  Our Specialized Services
</h2>

{/* Subtitle */}
<p
  className="mt-4 text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto"
  style={{ color: COLORS.textSub }}
>
  Structured care programs designed to support long-term health,
  daily comfort, and family confidence.
</p>

  </div>
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12">
        {loading ? (
          <p className="text-center col-span-full">Loading services...</p>
        ) : servicesList.length === 0 ? (
          <p className="text-center col-span-full">No services currently available.</p>
        ) : (
          servicesList.map((item, index) => {
            const blobColor = BLOB_COLORS[index % BLOB_COLORS.length];
            return (
              <motion.div
                key={item.id || index}
                initial={{
                  opacity: 0,
                  x: index % 2 === 0 ? -150 : 150,
                  scale: 0.95,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                  scale: 1,
                }}
                transition={{
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                }}
                viewport={{ once: true }}
                className="relative bg-white rounded-3xl p-5 sm:p-8 lg:p-10 shadow-lg overflow-hidden"
              >
                {/* Background Blob */}
                <div
                  className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-20"
                  style={{ background: blobColor }}
                />

                <div className="relative flex flex-col lg:flex-row items-center gap-8">
                  {/* TEXT SIDE */}
                  <div className="flex-1">
                    <h2
                      className="text-3xl font-bold"
                      style={{ color: COLORS.textMain }}
                    >
                      {item.name}{" "}
                      {item.care_category && (
                        <span style={{ color: blobColor }}>{item.care_category}</span>
                      )}
                    </h2>

                    <ServiceDescription description={item.long_description} />

                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                        `${WHATSAPP_MESSAGE} ${item.name}`,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`whatsapp-btn blob-${index}`}
                    >
                      <FaWhatsapp size={16} />
                      WhatsApp Enquiry
                    </a>
                  </div>

                  {/* IMAGE SIDE */}
                  <div className="flex-1 w-full">
                    {item.images ? (
                      <img
                        src={getMediaUrl(item.images)}
                        alt={item.name}
                        className="rounded-2xl shadow-md w-full object-cover h-48 sm:h-56 lg:h-auto"
                      />
                    ) : (
                      <div className="rounded-2xl shadow-md bg-gray-100 h-56 w-full flex items-center justify-center text-gray-400">
                        No Image Available
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </section>
    {/* <div className="absolute bottom-0 left-0 w-full translate-y-full">
  <SectionDivider position="bottom" color="#F7FAF9" height={90} />
</div> */}
</>
  );
};

export default Services;
